import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  ButtonPrimary,
  Checkbox,
  ImageInput,
  Input,
  Label,
  SelectInputSearch,
} from '@skf/bt-wind-rail-commonlibrary';
import { IOption } from '@skf/bt-wind-rail-commonlibrary/dist/components/form/types';

import { StoreState } from '../../../store/store';
import { ISectionsState } from '../../../store/sections/store';
import style from './SectionManagement.module.scss';
import { useLoadingScreen } from '../../../hooks/useLoadingScreen';
import {
  AcceptedImgTypes,
  Section,
  SectionFilter,
  getEmptySection,
} from '../../../store/sections/types';
import { useToast } from '../../../hooks/useToast';
import { SectionFilters } from './sectionFilters/SectionFilters';

interface ISectionFormProps {
  actionButtonText: string;
  editMode: boolean;
  viewMode?: boolean;
  existingSection?: Section;
  enableActionButtonCondition: (section: Section) => boolean;
  actionButtonClick: (section: Section) => void;
}

export const SectionManagementForm: React.FunctionComponent<ISectionFormProps> = ({
  actionButtonText,
  editMode,
  existingSection,
  viewMode = false,
  enableActionButtonCondition,
  actionButtonClick,
}) => {
  const { t } = useTranslation();
  const { setLoading } = useLoadingScreen();
  const { addToast } = useToast();

  const sectionsState = useSelector<StoreState, ISectionsState>(
    (state) => state.sections
  );
  const [newSection, setNewSection] = useState<Section>(getEmptySection());
  const [newUrlCharacterAdded, setNewUrlCharacterAdded] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      setLoading(false);
    };
  }, []);

  useEffect(() => {
    setLoading(sectionsState.loading);
  }, [sectionsState.loading]);


  useEffect(() => {
    if (existingSection) {
      const existingSectionTransformed: Section = { ...existingSection };
      if (
        existingSectionTransformed.userWebRoleName &&
        existingSectionTransformed.userWebRoleName.length > 0
      ) {
        existingSectionTransformed.userWebFeatureGrant = existingSectionTransformed.userWebRoleName.concat(
          '_view'
        );
      }
      setNewSection({
        ...existingSectionTransformed,
        oldIndustry: existingSection.industry,
        oldPortalName: existingSection.portalName,
        oldPowerBIReportName: existingSection.powerBIReportName,
        oldWebSectionDisplayOrder: existingSection.webSectionDisplayOrder,
        oldWebSectionParentName: existingSection.webSectionParentName,
        addQuickLinks: existingSection.addQuickLinks,
        quickLinkDescription: existingSection.quickLinkDescription,
        quickLinkTextDisplay: existingSection.quickLinkTextDisplay,
      });
    }
  }, [existingSection]);

  const getNoneOption = (): IOption => {
    return {
      label: t('None'),
      labelToShow: t('None'),
      value: '',
    };
  };

  const getIndustryOptions = (): IOption[] => {
    const industryOptions = sectionsState.industryInfo.map((x) => {
      return {
        label: x.industry,
        labelToShow: x.industry,
        value: x.industry,
      };
    });
    return industryOptions;
  };

  const onIndustryChange = (option: IOption | undefined) => {
    setNewSection({
      ...newSection,
      industry: option?.value.toString() || '',
      portalName: '',
      webSectionParentName: '',
    });
  };

  const getPortalOptions = (): IOption[] => {
    return (
      sectionsState.industryInfo
        .find((x) => x.industry === newSection.industry)
        ?.portalInformation.map((portal) => {
          return {
            label: portal.portalName,
            labelToShow: portal.portalName,
            value: portal.portalName,
          };
        }) || []
    );
  };

  const onPortalChange = (option: IOption | undefined) => {
    setNewSection({
      ...newSection,
      portalName: option?.value.toString() || '',
      webSectionParentName: '',
    });
  };

  const getPortalNameOptions = (): IOption[] => {
    const portalNameOptions =
      sectionsState.industryInfo
        .find((x) => x.industry === newSection.industry)
        ?.portalInformation.find((y) => y.portalName === newSection.portalName)
        ?.webSectionName.map((webSection) => {
          return {
            label: webSection.webSectionParentDisplayName,
            labelToShow: webSection.webSectionParentDisplayName,
            value: webSection.webSectionParentId,
          };
        }) || [];
    return [getNoneOption(), ...portalNameOptions];
  };

  const onPortalNameChange = (option: IOption | undefined) => {
    setNewSection({
      ...newSection,
      webSectionParentName: option?.value.toString() || '',
    });
  };

  const handleFileUploadInput = (e: any) => {
    if (e && e.target && e.target.files.length) {
      const file = (e.target as HTMLInputElement).files![0];
      const fileExtension = file.name
        .substring(file.name.lastIndexOf('.'))
        .toLowerCase();
      const isDocument = AcceptedImgTypes.indexOf(fileExtension) == -1;
      const extensionNotValid =
        AcceptedImgTypes.indexOf(fileExtension) == -1 || isDocument;
      if (extensionNotValid) {
        const errorMessage = isDocument
          ? t('Extension_NotValid')
          : t('Error_adding_image');
        addToast({
          message: errorMessage,
          status: 'danger',
          datetime: Date.now(),
        });
        return;
      }
      setNewSection({
        ...newSection
      });
      e.target.value = '';
    }
  };

  const handleURLChange = (e: string) => {
    let newUrl = e;
    if (e.startsWith('/') === false && newUrlCharacterAdded === false) {
      newUrl = `/${newUrl}`;
      setNewUrlCharacterAdded(true);
    }
    setNewSection({ ...newSection, webSectionURL: newUrl });
  };

  const handleUserWebRoleChange = (e: string) => {
    const userWebFeature = e.concat('_view');
    setNewSection({
      ...newSection,
      userWebRoleName: e,
      userWebFeatureGrant: userWebFeature,
    });
  };

  const handleDisplayOrderChange = (e: string) => {
    setNewSection({
      ...newSection,
      webSectionDisplayOrder: e,
    });
  };

  const existUserWebRole = () => {
    if (existingSection?.userWebRoleName)
      return existingSection?.userWebRoleName.length > 0;
    else return false;
  };

  const onAddFilter = (newFilter: SectionFilter) => {
    setNewSection({
      ...newSection,
      sectionFilterList: [...(newSection.sectionFilterList ?? []), newFilter],
    });
  };

  const onDeleteFilter = (filterToDelete: SectionFilter) => {
    const currentFilters = newSection.sectionFilterList ?? [];

    const deleteFilterIndex = currentFilters.findIndex(
      (x) => x.webSectionFilterName === filterToDelete.webSectionFilterName
    );
    if (deleteFilterIndex !== -1) {
      const filtersAfterDelete = [...currentFilters];
      filtersAfterDelete.splice(deleteFilterIndex, 1);
      setNewSection({
        ...newSection,
        sectionFilterList: filtersAfterDelete,
      });
    }
  };

  return (
    <div className={`${style.sectionAddContainer}`}>
      <div className={style.industrySectionContainer}>
        <div className={style.industryInfoContainer}>
          <SelectInputSearch
            className={style.sectionSelect}
            options={getIndustryOptions()}
            onSelectedItemChanged={onIndustryChange}
            placeHolder={t('Industry')}
            value={newSection.industry}
          />
          <SelectInputSearch
            className={style.sectionSelect}
            options={getPortalOptions()}
            onSelectedItemChanged={onPortalChange}
            placeHolder={t('Portal_Name')}
            isDisabled={newSection.industry === ''}
            value={newSection.portalName}
          />
          <SelectInputSearch
            className={style.sectionSelect}
            options={getPortalNameOptions()}
            onSelectedItemChanged={onPortalNameChange}
            placeHolder={t('WebSection_ParentName')}
            isDisabled={newSection.portalName === ''}
            value={newSection.webSectionParentName}
          />
        </div>
        <div className={style.rightSideContainer}>
          <div className={style.sectionFormContainer}>
            <div className={style.inputLabel}>
              <Label>{t('WebSection_Name')}</Label>
              <Input
                disabled={editMode}
                type="text"
                className={style.inputSection}
                value={newSection.webSectionName}
                onChange={(e) =>
                  setNewSection({ ...newSection, webSectionName: e })
                }
              />
            </div>
            <div className={style.inputLabel}>
              <Label>{t('WebSection_DisplayName')}</Label>
              <Input
                type="text"
                className={style.inputSection}
                value={newSection.webSectionDisplayName}
                onChange={(e) =>
                  setNewSection({ ...newSection, webSectionDisplayName: e })
                }
              />
            </div>
            <div className={style.inputLabel}>
              <Label>{t('WebSection_URL')}</Label>
              <Input
                type="text"
                className={style.inputSection}
                value={newSection.webSectionURL}
                onChange={handleURLChange}
              />
            </div>
            <div className={style.inputLabel}>
              <Label>{t('WebSection_ReportName')}</Label>
              <Input
                type="text"
                className={style.inputSection}
                value={newSection.powerBIReportName}
                onChange={(e) =>
                  setNewSection({ ...newSection, powerBIReportName: e })
                }
              />
            </div>
            <div className={style.inputLabel}>
              <Label>{t('WebSection_ReportDefaultPage')}</Label>
              <Input
                type="text"
                className={style.inputSection}
                value={newSection.powerBIReportDefaultPage}
                onChange={(e) =>
                  setNewSection({ ...newSection, powerBIReportDefaultPage: e })
                }
              />
            </div>
            <div className={style.inputLabel}>
              <Label>{t('WebSection_UserWebRoleName')}</Label>
              <Input
                disabled={existUserWebRole()}
                type="text"
                className={style.inputSection}
                value={newSection.userWebRoleName}
                onChange={handleUserWebRoleChange}
              />
            </div>
            <div className={style.inputLabel}>
              <Label>{t('WebSection_DisplayOrderName')}</Label>
              <Input
                type="number"
                className={style.inputSection}
                value={newSection.webSectionDisplayOrder}
                onChange={(e) =>
                  setNewSection({ ...newSection, webSectionDisplayOrder: e })
                }
              />
            </div>
          </div>
          <div className={style.messagesContainer}>
            <div className={style.messageBox}>
              {t('WebSection_NoUrlMessage')}
            </div>
          </div>
        </div>
      </div>
      <div className={style.checkBoxSectionContainer}>
        <div className={style.checkBoxSection}>
         
          <Checkbox 
          square
          isChecked = {newSection.webSectionSKFOnly}
          className={style.inputSection}
          onClick={() =>
            setNewSection({
              ...newSection,
              webSectionSKFOnly: !newSection.webSectionSKFOnly,
            })
          }
          />
           <Label>{t('WebSection_SKFOnly')}</Label>
        </div>
        <div className={style.checkBoxSection}>
          <Checkbox
            square
            isChecked={newSection.powerBIReportDisplayTabs}
            onClick={() =>
              setNewSection({
                ...newSection,
                powerBIReportDisplayTabs: !newSection.powerBIReportDisplayTabs,
              })
            }
          />
          <label>{t('Show_Report_Tabs')}</label>
        </div>
        <div className={style.checkBoxSection}>
          <Checkbox
            square
            isChecked={newSection.addQuickLinks}
            onClick={() =>
              setNewSection({
                ...newSection,
                addQuickLinks: !newSection.addQuickLinks,
              })
            }
          />
          <label>{t('Has_Quick_Link')}</label>
        </div>
      </div>
      {newSection.addQuickLinks && (
        <>
          <h1 className={style.sectionTitle}>{t('Section_QuickLink')}</h1>
          <div className={style.quickLinkContainer}>
            <div className={style.sectionFormContainer}>
              <div className={style.inputLabel}>
                <Label>{t('WebSection_QuickLinkTitle')}</Label>
                <Input
                  type="text"
                  className={style.inputSection}
                  value={newSection.quickLinkTextDisplay}
                  onChange={(e) =>
                    setNewSection({ ...newSection, quickLinkTextDisplay: e })
                  }
                />
              </div>
              <div className={style.inputLabel}>
                <Label>{t('WebSection_QuickLinkDescription')}</Label>
                <Input
                  type="text"
                  className={style.inputSection}
                  value={newSection.quickLinkDescription}
                  onChange={(e) =>
                    setNewSection({ ...newSection, quickLinkDescription: e })
                  }
                />
              </div>
            </div>
          </div>
        </>
      )}
      <SectionFilters
        sectionFilters={newSection.sectionFilterList ?? []}
        onAddFilter={onAddFilter}
        onDeleteFilter={onDeleteFilter}
      />
      {viewMode === false && (
        <ButtonPrimary
          className={style.addSectionButton}
          disabled={enableActionButtonCondition(newSection)}
          onClick={() => actionButtonClick(newSection)}
          text={t(actionButtonText)}
        />
      )}
    </div>
  );
};
